import React, { useState, useEffect } from 'react';
import Map, { Marker, Source, Layer, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPin } from 'lucide-react';
import proj4 from 'proj4';

// Define the coordinate systems
const bngProj = '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs';
const wgs84Proj = '+proj=longlat +datum=WGS84 +no_defs';

interface MapboxLocationProps {
    latitude: number;
    longitude: number;
    zoom?: number;
    mapboxToken: string;
    geoJsonPath?: string;
}

const transformCoordinates = (coords: number[]): number[] => {
    return proj4(bngProj, wgs84Proj, coords);
};

const transformGeoJSON = (geojson: any): any => {
    if (!geojson) return null;

    const transformed = JSON.parse(JSON.stringify(geojson)); // Deep clone

    const transformGeometry = (geometry: any) => {
        if (!geometry) return;

        if (geometry.type === 'Point') {
            geometry.coordinates = transformCoordinates(geometry.coordinates);
        } else if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
            geometry.coordinates = geometry.coordinates.map(transformCoordinates);
        } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
            geometry.coordinates = geometry.coordinates.map((ring: number[][]) => 
                ring.map(transformCoordinates)
            );
        } else if (geometry.type === 'MultiPolygon') {
            geometry.coordinates = geometry.coordinates.map((polygon: number[][][]) =>
                polygon.map((ring: number[][]) => ring.map(transformCoordinates))
            );
        }
    };

    if (transformed.type === 'Feature') {
        transformGeometry(transformed.geometry);
    } else if (transformed.type === 'FeatureCollection') {
        transformed.features.forEach((feature: any) => {
            transformGeometry(feature.geometry);
        });
    }

    return transformed;
};

const MapboxLocation: React.FC<MapboxLocationProps> = ({
    latitude,
    longitude,
    zoom = 14,
    mapboxToken,
    geoJsonPath
}) => {
    const [viewState, setViewState] = useState({
        latitude,
        longitude,
        zoom
    });

    const [geoJsonData, setGeoJsonData] = useState<any>(null);
    const [showOutline, setShowOutline] = useState(true);

    useEffect(() => {
        if (geoJsonPath) {
            fetch(geoJsonPath)
                .then(response => response.json())
                .then(data => {
                    const transformedData = transformGeoJSON(data);
                    setGeoJsonData(transformedData);
                })
                .catch(error => console.error('Error loading GeoJSON:', error));
        }
    }, [geoJsonPath]);

    const layerStyle = {
        id: 'field-boundary',
        type: 'fill',
        paint: {
            'fill-color': '#FF7260',
            'fill-opacity': 0.5,
            'fill-outline-color': '#51B3AC',
            'fill-outline-width': 2
        }
    } as any;

    return (
        <div className="relative w-full h-60 rounded-lg overflow-hidden shadow-md">
            <Map
                {...viewState}
                onMove={evt => setViewState(evt.viewState)}
                mapboxAccessToken={mapboxToken}
                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                style={{ width: '100%', height: '100%' }}
            >
                <NavigationControl position="top-right" />
                
                <Marker longitude={longitude} latitude={latitude} anchor="center">
                    <div className="relative">
                        {/* Pulsating background */}
                        <div className="absolute -inset-4 bg-pink/20 rounded-full animate-pulse" />
                        {/* Solid marker */}
                        <div className="relative w-4 h-4 bg-pink rounded-full border-2 border-white shadow-lg" />
                    </div>
                </Marker>

                {showOutline && geoJsonData && (
                    <Source type="geojson" data={geoJsonData}>
                        <Layer {...layerStyle} />
                    </Source>
                )}
            </Map>

            {/* Toggle Button Overlay */}
            <button
                onClick={() => setShowOutline(!showOutline)}
                className="absolute top-3 left-3 px-3 py-2 bg-white/90 hover:bg-white text-gray-700 text-sm font-medium rounded-md shadow-md transition-colors duration-200 flex items-center space-x-1"
            >
                <svg 
                    className={`w-4 h-4 ${showOutline ? 'text-pink' : 'text-gray-400'}`} 
                    fill="none" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                >
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>{showOutline ? 'Hide' : 'Show'} Field Outline</span>
            </button>
        </div>
    );
};

export default MapboxLocation;